import {
  FlexBoxImageScaleView,
  FlexBoxView,
  getFlexNode,
} from '@play-co/timestep-core/flexbox';
import uiConfig from 'src/lib/ui/config';
import i18n from 'src/lib/i18n/i18n';
import animate from '@play-co/timestep-core/lib/animate';
import { animDuration, isPlatformGuest } from 'src/lib/utils';
import { ImageView, View } from '@play-co/timestep-core/ui';
import LangBitmapFontTextView from 'src/lib/ui/components/LangBitmapFontTextView';
import FriendsActionButton from './FriendsActionButton';
import sounds from 'src/lib/sounds';
import StateObserver, { now } from 'src/StateObserver';
import {
  hasMetGiftsUnlockCriteria,
  isGiftsFeatureUnlocked,
} from '../gifts/helpers';
import * as Visit from '../../replicant/visit';
import * as Social from 'src/replicant/social';
import features from 'src/replicant/ruleset/features';
import * as Duo from 'src/replicant/duo';
import { createEmitter } from 'src/lib/Emitter';
import { GCInstant as platform } from 'src/lib/gcinstant';
import * as Fate from '../../replicant/fate';
import { registerHandTarget } from '../tutorial/services/TutorialHandService';
import { isTeamFeatureUnlocked } from 'src/replicant/team/team.getters';
import { dreamDriftIsInstanceActive } from 'src/replicant/dreamDrift/dreamDrift.getters';

const ui = uiConfig.buruburu.menu;

export default class FriendsMenu extends FlexBoxImageScaleView {
  constructor() {
    super(ui.menu);

    this.image.onInputStart = () => this.emit('Close');
    const onSelect = this.onSelect.bind(this);
    const closeMenu = this.closeMenu.bind(this);

    const { user } = StateObserver.getState();
    const { duo, teams } = features(user);
    const isDuoEnabled = isPlatformGuest(platform) || duo;

    // Create rows
    const row3 = this.addSubview(new FlexBoxView(ui.menuRow));
    const row2 = this.addSubview(new FlexBoxView(ui.menuRow));
    const row1 = this.addSubview(new FlexBoxView(ui.menuRow));

    // Row 3
    if (teams && isTeamFeatureUnlocked(user)) {
      const teamButton = row3.addSubview(
        new FriendsActionButton({
          type: 'teamCafe',
          onSelect,
          closeMenu,
        }),
      );

      registerHandTarget('teamButton', teamButton);
    }

    const isDuoLocked = isPlatformGuest(platform)
      ? !Duo.getters.hasMetUnlockCriteria(user)
      : !Duo.getters.isUnlocked(user);

    if (isDuoEnabled) {
      row2.addSubview(
        new FriendsActionButton({
          type: 'duoReceivedList',
          onSelect,
          closeMenu,
          isLocked: isDuoLocked,
        }),
      );
    }

    const visitorList = row2.addSubview(
      new FriendsActionButton({
        type: 'visitorList',
        onSelect,
        closeMenu,
      }),
    );

    const isGiftLocked = isPlatformGuest(platform)
      ? !hasMetGiftsUnlockCriteria(user)
      : !isGiftsFeatureUnlocked(user);
    const giftIcon = new FriendsActionButton({
      type: 'gift',
      onSelect,
      closeMenu,
      isLocked: isGiftLocked,
    });
    row2.addSubview(giftIcon);

    row1.addSubview(
      new FriendsActionButton({
        type: 'addFriends',
        onSelect,
        closeMenu,
      }),
    );

    const isInviteLocked = isPlatformGuest(platform)
      ? !Social.getters.hasMetUnlockCriteria(user)
      : !Social.getters.isFriendInviteAvailable(user);
    const invite = new FriendsActionButton({
      type: 'invite',
      onSelect,
      closeMenu,
      isLocked: isInviteLocked,
    });

    const visitFriend = new FriendsActionButton({
      type: 'visitFriend',
      onSelect,
      closeMenu,
    });

    row1.addSubview(
      new FriendsActionButton({
        type: 'friendRequests',
        onSelect,
        closeMenu,
      }),
    );
    invite && row1.addSubview(invite);

    createEmitter(this, ({ user }) => {
      const currentTime = now();
      return {
        isFriendVisitDisabled: isPlatformGuest(platform)
          ? !Visit.getters.hasMetUnlockCriteria(user)
          : !Visit.getters.isUnlocked(user),
        petState: Fate.getters.getVisiblePetState(user, currentTime),
        isDreamDriftActive: dreamDriftIsInstanceActive(user, currentTime),
      };
    }).addListener(
      ({ petState, isDreamDriftActive, isFriendVisitDisabled }) => {
        if (petState === 'dead') {
          visitorList.setLocked(true, 'dead');
        } else if (petState === 'sleeping') {
          visitorList.setLocked(true, 'sleeping');
        } else if (isDreamDriftActive) {
          visitorList.setLocked(true, 'dreamDrift');
        } else {
          visitorList.setLocked(isFriendVisitDisabled, 'visitFriend');
          visitFriend.setLocked(isFriendVisitDisabled);
        }
      },
    );

    const closeHint = this.addSubview(new ImageView(ui.closeHintBg));
    closeHint.canHandleEvents(false, true);

    getFlexNode(closeHint).updateStyle({ marginTop: 23 });
    closeHint.addSubview(
      new LangBitmapFontTextView({
        ...ui.closeHintText,
        localeText: () => i18n('footer.tapToClose'),
        width: closeHint.style.width,
        height: closeHint.style.height,
      }),
    );
  }

  public fadeIn(superview: View) {
    animate(this)
      .clear()
      .now(() =>
        this.updateOpts({
          superview,
          visible: true,
          opacity: 0,
          offsetY: 100,
        }),
      )
      .then({ opacity: 1, offsetY: 0 }, animDuration, animate.easeOut);
  }

  public fadeOut() {
    animate(this)
      .clear()
      .then({ opacity: 0, offsetY: 100 }, animDuration, animate.easeOut)
      .then(() => {
        this.removeFromSuperview();
      });
  }

  private onSelect() {
    sounds.sfx.play('ok');
  }

  private closeMenu() {
    this.emit('Close');
  }
}
